import React, { useState } from 'react';
import { FiCheck, FiChevronDown, FiGlobe } from 'react-icons/fi';
import { PUBLISHED_STATUS } from '../../../../model/FestivalData';
import { useLanguage, useMessages } from '../../../../i18n/I18n';
import './SaveButtonsContainer.css';

interface SaveButtonsContainerProps {
  unsavedChanges: Set<string>;
  lastSaveTime: Date | null;
  isSaving: boolean;
  onSave: () => void;
  status: PUBLISHED_STATUS;
  /** Asks to publish a draft (saving it at the same time) */
  onPublish: () => void;
  /** Asks to take a published version back to draft (saving it at the same time) */
  onUnpublish: () => void;
}

const SaveButtonsContainer: React.FC<SaveButtonsContainerProps> = ({
  unsavedChanges,
  lastSaveTime,
  isSaving,
  onSave,
  status,
  onPublish,
  onUnpublish,
}) => {
  const t = useMessages('editor');
  const { locale } = useLanguage();
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const hasUnsavedChanges = unsavedChanges.size > 0;

  const getSaveStatus = () => {
    if (hasUnsavedChanges) {
      return t.unsavedCount(unsavedChanges.size);
    } else if (lastSaveTime) {
      return t.allSavedAt(lastSaveTime.toLocaleTimeString(locale));
    }
    return '';
  };

  return (
    <div className="save-bar">
      {status === 'draft' ? (
        <span className="status-button draft version-status">{t.draft}</span>
      ) : (
        <div className="status-dropdown-container">
          <button
            type="button"
            className="status-button published version-status"
            aria-haspopup="menu"
            aria-expanded={showStatusDropdown}
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
          >
            {t.published}
            <FiChevronDown aria-hidden="true" />
          </button>
          {showStatusDropdown && (
            <div className="status-dropdown" role="menu">
              <button type="button" role="menuitem" onClick={() => {
                setShowStatusDropdown(false);
                onUnpublish();
              }}>{t.backToDraft}</button>
            </div>
          )}
        </div>
      )}
      <span className={`save-status-badge${hasUnsavedChanges ? ' unsaved' : lastSaveTime ? ' saved' : ' empty'}`}>
        {hasUnsavedChanges && <span className="save-status-dot" aria-hidden="true" />}
        {!hasUnsavedChanges && lastSaveTime && <FiCheck aria-hidden="true" />}
        <span className="save-status">{getSaveStatus()}</span>
      </span>
      <button
        type="button"
        onClick={onSave}
        disabled={isSaving || !hasUnsavedChanges}
        className="save-bar-button"
      >
        {isSaving ? t.saving : t.saveChanges}
      </button>
      {status === 'draft' && (
        <button type="button" onClick={onPublish} disabled={isSaving} className="publish-button">
          <FiGlobe aria-hidden="true" />
          {t.publish}
        </button>
      )}
    </div>
  );
};

export default SaveButtonsContainer;
